import { useState } from "react";
import { Banknote, CreditCard, DollarSign, Gift, Smartphone } from "lucide-react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@/components/ui";
import { formatCurrency } from "@/lib/utils";
import type { Order, PaymentMethod } from "@/types";

interface PaymentRequiredDialogProps {
  order: Order;
  onClose: () => void;
  onPay: (
    method: PaymentMethod,
    amountTendered: number,
    referenceNumber?: string,
  ) => void;
  completeOnPay: boolean;
  isPending: boolean;
}

const QUICK_AMOUNTS = [5, 10, 20, 50, 100];

export function PaymentRequiredDialog({
  order,
  onClose,
  onPay,
  completeOnPay,
  isPending,
}: PaymentRequiredDialogProps) {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("cash");
  const [amountTendered, setAmountTendered] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");

  const total = order.total;
  const isFullyComped = total === 0;
  const change = Math.max(0, (parseFloat(amountTendered) || 0) * 100 - total);

  const handleSubmit = () => {
    if (isFullyComped) {
      onPay("comp", 0);

      return;
    }

    const amount =
      paymentMethod === "cash"
        ? (parseFloat(amountTendered) || 0) * 100
        : total;
    onPay(paymentMethod, amount, referenceNumber || undefined);
  };

  const cashShort =
    !isFullyComped &&
    paymentMethod === "cash" &&
    (parseFloat(amountTendered) || 0) * 100 < total;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-amber-600" />
            {completeOnPay ? "Collect Payment" : "Record Payment"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg bg-primary/5 p-4 text-center">
            <p className="text-sm text-muted-foreground">
              Order #{order.orderNumber}
            </p>
            <p className="mt-1 text-3xl font-bold">{formatCurrency(total)}</p>
          </div>
          {isFullyComped ? (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-center">
              <Gift className="mx-auto mb-2 h-8 w-8 text-green-600" />
              <p className="font-semibold text-green-800">Order Fully Comped</p>
              <p className="mt-1 text-sm text-green-600">
                All items are complimentary — no payment required.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant={paymentMethod === "cash" ? "default" : "outline"}
                  className="flex-col gap-1 py-4"
                  onClick={() => setPaymentMethod("cash")}
                >
                  <Banknote className="h-5 w-5" />
                  <span className="text-xs">Cash</span>
                </Button>
                <Button
                  variant={paymentMethod === "card" ? "default" : "outline"}
                  className="flex-col gap-1 py-4"
                  onClick={() => setPaymentMethod("card")}
                >
                  <CreditCard className="h-5 w-5" />
                  <span className="text-xs">Card</span>
                </Button>
                <Button
                  variant={paymentMethod === "mobile" ? "default" : "outline"}
                  className="flex-col gap-1 py-4"
                  onClick={() => setPaymentMethod("mobile")}
                >
                  <Smartphone className="h-5 w-5" />
                  <span className="text-xs">Mobile</span>
                </Button>
              </div>
              {paymentMethod === "cash" && (
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">
                      Amount Tendered
                    </Label>
                    <Input
                      type="number"
                      placeholder="e.g., 20.00"
                      value={amountTendered}
                      onChange={(e) => setAmountTendered(e.target.value)}
                      className="mt-1 text-lg"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {QUICK_AMOUNTS.map((amount) => (
                      <Button
                        key={amount}
                        variant="outline"
                        size="sm"
                        onClick={() => setAmountTendered(String(amount))}
                      >
                        ${amount}
                      </Button>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setAmountTendered(String(total / 100))}
                    >
                      Exact
                    </Button>
                  </div>
                  {change > 0 && (
                    <div className="rounded-lg bg-green-50 p-3 text-center">
                      <p className="text-xs text-green-600">Change Due</p>
                      <p className="text-2xl font-bold text-green-700">
                        {formatCurrency(change)}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {paymentMethod !== "cash" && (
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Reference (optional)
                  </Label>
                  <Input
                    placeholder="Transaction ID"
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    className="mt-1"
                  />
                </div>
              )}
            </>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isPending || cashShort}>
            {isPending
              ? "Processing..."
              : completeOnPay
                ? isFullyComped
                  ? "Confirm Comp & Complete"
                  : "Pay & Complete"
                : isFullyComped
                  ? "Confirm Comp"
                  : "Record Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
